import { useMutation } from "@tanstack/react-query";
import merge from "lodash/merge";
import set from "lodash/set";

import { queryClient } from "@/api/core";
import { AuthQueryKeys } from "@/contexts/auth/query-keys";
import { useAuth } from "@/contexts/auth/useAuth";
import { useApi } from "@/hooks";
import { accountSchema } from "@/schemas";
import toast from "@/toast";

import type { ApiError } from "@/types/api";
import type { AxiosError, RawAxiosRequestHeaders } from "axios";

type UpdateCredentialsData = {
  accepted_status_edit_prompt?: boolean;
  accepts_chat_messages?: boolean;
  avatar?: File;
  bookmarks_onboarded?: boolean;
  display_name?: string;
  group_reactions_onboarded?: boolean;
  feeds_onboarded?: boolean;
  header?: File;
  location?: string;
  note?: string;
  pleroma_settings_store?: {
    soapbox_fe?: {
      chats?: { mainWindow?: string };
      displayMedia?: string;
      themeMode?: string;
      videoPlayer?: {
        volume?: number;
        muted?: boolean;
      };
    };
  };
  show_nonmember_group_statuses?: boolean;
  tv_onboarded?: boolean;
  unauth_visibility?: boolean;
  website?: string;
  receive_marketing_message_alerts?: boolean;
};

// TODO: fix issue with normalizing pleroma -> pleroma_settings
const useUpdateCredentials = (opts?: { headers: RawAxiosRequestHeaders }) => {
  const { headers = { "Content-Type": "multipart/form-data" } } = opts || {};

  const { currentAccount, updateCurrentAccount } = useAuth();
  const api = useApi();

  return useMutation({
    mutationFn: (data: UpdateCredentialsData) =>
      api.patch("/api/v1/accounts/update_credentials", data, { headers }),
    onMutate(variables) {
      const cachedAccount = currentAccount;
      const isUpdatingSettings = !!variables.pleroma_settings_store;
      let newSettings: any = { ...variables };
      if (isUpdatingSettings) {
        newSettings = set(
          newSettings,
          ["pleroma", "settings_store", "soapbox_fe"],
          variables.pleroma_settings_store?.soapbox_fe,
        );
      }

      updateCurrentAccount(
        accountSchema.parse(merge({ ...currentAccount, ...newSettings })),
      );

      return { cachedAccount };
    },
    onSuccess(response) {
      const parsedAccount = accountSchema.parse(response.data);

      queryClient.setQueryData(
        AuthQueryKeys.user(currentAccount!.id),
        parsedAccount,
      );
      updateCurrentAccount(parsedAccount);
    },
    onError(error: AxiosError<ApiError>, _variables, context: any) {
      if (error.response?.data) {
        const message = error.response.data.errors[0].error_message;
        toast.error(message);
      }

      updateCurrentAccount(context.cachedAccount);
    },
  });
};

export { useUpdateCredentials, type UpdateCredentialsData };
